import React, { useMemo, useState } from "react";
import {
    Paper,
    Box,
    Typography,
    Stack,
    Checkbox,
    FormControlLabel,
    Chip,
    TextField,
    InputAdornment,
} from "@mui/material";
import { Search, LockOpen } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

/**
 * Table-style layout for permission selection.
 * - `grouped` is { moduleName: Permission[] }
 * - `selectedIds` is a Set or Array of selected permission ids
 * - `onChange(nextIds)` is called with the updated array of selected ids
 * - `disabled` greys-out the whole tree
 *
 * Layout:
 *   ┌────────────────────────────────────────────────┐
 *   │ ☐ Check all permissions                        │
 *   ├──────────────┬─────────────────────────────────┤
 *   │ Module       │ Available permissions           │
 *   ├──────────────┼─────────────────────────────────┤
 *   │ Agents       │ ☐ Archive  ☐ Create  ☐ Delete   │
 *   │ ☐ Check all  │ ☐ Edit     ☐ Show    ...        │
 *   ├──────────────┼─────────────────────────────────┤
 *   │ Airlines     │ ...                             │
 */
const PermissionsTree = ({ grouped = {}, selectedIds = [], onChange, disabled }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");

    const [filter, setFilter] = useState("");

    const selectedSet = useMemo(() => new Set(selectedIds.map(Number)), [selectedIds]);

    const modules = useMemo(() => Object.keys(grouped).sort(), [grouped]);

    const filteredGrouped = useMemo(() => {
        if (!filter) return grouped;
        const f = filter.toLowerCase();
        const out = {};
        for (const m of modules) {
            const matches = grouped[m].filter((p) =>
                (p.name || "").toLowerCase().includes(f) ||
                (p.label?.en || "").toLowerCase().includes(f) ||
                (p.label?.ar || "").includes(filter)
            );
            if (matches.length) out[m] = matches;
        }
        return out;
    }, [grouped, filter, modules]);

    const filteredModules = Object.keys(filteredGrouped).sort();

    const togglePermission = (id) => {
        const next = new Set(selectedSet);
        if (next.has(Number(id))) next.delete(Number(id));
        else next.add(Number(id));
        onChange(Array.from(next));
    };

    const moduleStats = (m) => {
        const ids = grouped[m]?.map((p) => p.id) || [];
        const selectedCount = ids.filter((id) => selectedSet.has(Number(id))).length;
        return { total: ids.length, selected: selectedCount, ids };
    };

    const toggleModuleAll = (m) => {
        const { ids, selected, total } = moduleStats(m);
        const next = new Set(selectedSet);
        if (selected === total) {
            ids.forEach((id) => next.delete(Number(id)));
        } else {
            ids.forEach((id) => next.add(Number(id)));
        }
        onChange(Array.from(next));
    };

    const totalSelected = selectedSet.size;
    const totalAll = modules.reduce((acc, m) => acc + (grouped[m]?.length || 0), 0);

    const allSelected = totalAll > 0 && totalSelected === totalAll;
    const someSelected = totalSelected > 0 && totalSelected < totalAll;

    const toggleAll = () => {
        if (allSelected) {
            onChange([]);
        } else {
            const allIds = modules.flatMap((m) =>
                (grouped[m] || []).map((p) => Number(p.id))
            );
            onChange(allIds);
        }
    };

    return (
        <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, opacity: disabled ? 0.6 : 1 }}
        >
            {/* Header — title + counter + search */}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ sm: "center" }}
                mb={2}
            >
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <LockOpen color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>
                        {isArabic ? "الصلاحيات" : "Permissions"}
                    </Typography>
                    <Chip
                        size="small"
                        label={`${totalSelected} / ${totalAll}`}
                        color="primary"
                        variant="tonal"
                        sx={{ fontWeight: "bold", borderRadius: "6px" }}
                    />
                </Stack>

                <TextField
                    size="small"
                    placeholder={isArabic ? "بحث في الصلاحيات..." : "Search permissions..."}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    sx={{ minWidth: 240 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Stack>

            {/* Master "Check all permissions" row */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 2,
                    mb: 1.5,
                    bgcolor: (t) =>
                        allSelected
                            ? t.palette.primary.main + "15"
                            : someSelected
                                ? t.palette.warning.main + "12"
                                : t.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.03)"
                                    : "rgba(0,0,0,0.02)",
                    border: (t) =>
                        `1px solid ${
                            allSelected
                                ? t.palette.primary.main + "55"
                                : t.palette.divider
                        }`,
                    cursor: disabled ? "not-allowed" : "pointer",
                    transition: "all 0.2s",
                }}
                onClick={() => !disabled && toggleAll()}
            >
                <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    disabled={disabled}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                    sx={{ p: 0.5, mr: 1 }}
                />
                <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>
                    {isArabic ? "تحديد كل الصلاحيات" : "Check all permissions"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {allSelected
                        ? (isArabic ? "تم التحديد بالكامل" : "All selected")
                        : someSelected
                            ? (isArabic
                                ? `${totalSelected} من ${totalAll} محددة`
                                : `${totalSelected} of ${totalAll} selected`)
                            : (isArabic ? "لا شيء محدد" : "Nothing selected")}
                </Typography>
            </Box>

            {/* Empty state */}
            {filteredModules.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
                    {isArabic ? "لا توجد صلاحيات مطابقة." : "No matching permissions."}
                </Typography>
            )}

            {filteredModules.length > 0 && (
                <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        bgcolor: "transparent",
                    }}
                >
                    {/* Table header */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "140px 1fr", md: "200px 1fr" },
                            bgcolor: (t) =>
                                t.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.04)"
                                    : "rgba(0,0,0,0.04)",
                            borderBottom: (t) => `1px solid ${t.palette.divider}`,
                        }}
                    >
                        <Box sx={{ px: 2, py: 1.25 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary">
                                {isArabic ? "الوحدة" : "Module"}
                            </Typography>
                        </Box>
                        <Box sx={{ px: 2, py: 1.25 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary">
                                {isArabic ? "الصلاحيات المتاحة" : "Available permissions"}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Module rows */}
                    {filteredModules.map((m, idx) => {
                        const items = filteredGrouped[m];
                        const { total, selected } = moduleStats(m);
                        const allModSelected = total > 0 && selected === total;
                        const someModSelected = selected > 0 && selected < total;
                        const isLast = idx === filteredModules.length - 1;

                        return (
                            <Box
                                key={m}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "140px 1fr", md: "200px 1fr" },
                                    borderBottom: (t) =>
                                        isLast ? "none" : `1px solid ${t.palette.divider}`,
                                    "&:hover": {
                                        bgcolor: (t) =>
                                            t.palette.mode === "dark"
                                                ? "rgba(255,255,255,0.02)"
                                                : "rgba(0,0,0,0.015)",
                                    },
                                }}
                            >
                                {/* Module name + Check all */}
                                <Box sx={{ px: 2, py: 2 }}>
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{ fontFamily: "monospace", color: "primary.main", mb: 0.75 }}
                                    >
                                        {m}
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={allModSelected}
                                                indeterminate={someModSelected}
                                                disabled={disabled}
                                                onChange={() => toggleModuleAll(m)}
                                                sx={{ p: 0.5 }}
                                            />
                                        }
                                        label={
                                            <Typography variant="caption" color="text.secondary">
                                                {isArabic ? "تحديد الكل" : "Check all"}
                                            </Typography>
                                        }
                                        sx={{ m: 0 }}
                                    />
                                    <Chip
                                        size="small"
                                        label={`${selected}/${total}`}
                                        color={allModSelected ? "primary" : (someModSelected ? "warning" : "default")}
                                        variant="tonal"
                                        sx={{
                                            mt: 0.75,
                                            fontWeight: "bold",
                                            borderRadius: "6px",
                                            minWidth: 50,
                                            display: "flex",
                                            width: "fit-content",
                                        }}
                                    />
                                </Box>

                                {/* Permissions grid */}
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "1fr",
                                            sm: "1fr 1fr",
                                            md: "1fr 1fr 1fr",
                                        },
                                        gap: 0.75,
                                        borderInlineStart: (t) => `1px solid ${t.palette.divider}`,
                                    }}
                                >
                                    {items.map((p) => {
                                        const label = typeof p.label === 'string' 
                                            ? p.label 
                                            : (p.label?.[i18n.language] ||
                                               p.label?.en ||
                                               p.label?.ar ||
                                               p.name);
                                        return (
                                            <FormControlLabel
                                                key={p.id}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedSet.has(Number(p.id))}
                                                        onChange={() => togglePermission(p.id)}
                                                        disabled={disabled}
                                                        sx={{ p: 0.5 }}
                                                    />
                                                }
                                                label={
                                                    <Box>
                                                        <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                                                            {label}
                                                        </Typography>
                                                        {label !== p.name && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                                                {p.name}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                                sx={{ m: 0, alignItems: "flex-start" }}
                                            />
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    })}
                </Paper>
            )}
        </Paper>
    );
};

export default PermissionsTree;
